import { ICON_OPTION_LABELS } from './iconOptionLabels';
import { SENDER_TYPES } from './senderTypes';

const BUTTON_CONTENT_TYPES = {
  ADAPTIVE: 'application/vnd.microsoft.card.adaptive',
  HERO: 'application/vnd.microsoft.card.hero',
};

const ACTIVITY_TYPES = {
  MESSAGE: 'message',
  TYPING: 'typing',
};

export const OPTION_TYPES = {
  ICON: 'icon',
  TEXT: 'text',
};

/**
 * A quick-reply style option shown as a selectable response in the chat UI.
 * @typedef {string} DirectLineOption
 */

/**
 * Minimal Direct Line activity subset required by chatbot mapping logic.
 * We intentionally constrain this shape to keep mapper behavior explicit and testable.
 * @typedef {Object} DirectLineActivityLike
 * @property {string} [id]
 * @property {string} [type]
 * @property {{ role?: string }} [from]
 * @property {string} [text]
 * @property {{ messageId?: string, feedbackTargetMessageId?: string, transcript_data?: { name?: string } }} [channelData]
 * @property {{ iconButtons?: string[], textButtons?: string[] }} [value]
 * @property {{ actions?: Array<{ title?: string }> }} [suggestedActions]
 * @property {Array<{ contentType?: string, content?: { actions?: Array<{ title?: string }>, buttons?: Array<{ title?: string }> } }>} [attachments]
 */

/**
 * Internal normalized chatbot message shape consumed by Redux/UI.
 * @typedef {Object} ChatMessage
 * @property {string} id
 * @property {'user'|'bot'} sender
 * @property {string} text
 * @property {DirectLineOption[]} [options]
 * @property {'icon'|'text'} [optionType]
 * @property {boolean} [isAiGenerated]
 * @property {boolean} [isFeedback]
 * @property {boolean} [isSignInPrompt]
 * @property {string} [feedbackTargetMessageId]
 * @property {string} [feedbackAcknowledgementTargetMessageId]
 */

const toUniqueStrings = values => {
  if (!Array.isArray(values)) {
    return [];
  }

  return [
    ...new Set(
      values.filter(value => typeof value === 'string' && value.trim()),
    ),
  ];
};

const getTitlesFromSuggestedActions = activity => {
  return toUniqueStrings(
    activity?.suggestedActions?.actions?.map(action => action?.title),
  );
};

const getTitlesFromAdaptiveCard = attachment => {
  if (attachment?.contentType !== BUTTON_CONTENT_TYPES.ADAPTIVE) {
    return [];
  }

  return toUniqueStrings(
    attachment?.content?.actions?.map(action => action?.title),
  );
};

const getTitlesFromHeroCard = attachment => {
  if (attachment?.contentType !== BUTTON_CONTENT_TYPES.HERO) {
    return [];
  }

  return toUniqueStrings(
    attachment?.content?.buttons?.map(button => button?.title),
  );
};

const getTitlesFromIconButtons = activity => {
  return toUniqueStrings(activity?.value?.iconButtons);
};

const getTitlesFromTextButtons = activity => {
  return toUniqueStrings(activity?.value?.textButtons);
};

export const getActivityOptions = activity => {
  const iconButtonTitles = getTitlesFromIconButtons(activity);
  if (iconButtonTitles.length) {
    return iconButtonTitles;
  }

  const textButtonTitles = getTitlesFromTextButtons(activity);
  if (textButtonTitles.length) {
    return textButtonTitles;
  }

  // Suggested actions are channel-native quick replies and should take precedence
  // over card-parsed fallbacks so user intent options match bot expectations.
  const suggestedActionTitles = getTitlesFromSuggestedActions(activity);
  if (suggestedActionTitles.length) {
    return suggestedActionTitles;
  }

  // Card button extraction is a compatibility fallback for bots that emit hero/adaptive
  // cards instead of suggestedActions, preserving option UX across bot implementations.
  return toUniqueStrings(
    (activity?.attachments || []).flatMap(attachment => [
      ...getTitlesFromAdaptiveCard(attachment),
      ...getTitlesFromHeroCard(attachment),
    ]),
  );
};

const getActivityOptionType = activity => {
  return getTitlesFromIconButtons(activity).length
    ? OPTION_TYPES.ICON
    : OPTION_TYPES.TEXT;
};

const TRANSCRIPT_NAMES = {
  PRR_SURVEY_ACKNOWLEDGEMENT: 'PRRSurveyAcknowledgement',
  PRR_SURVEY_PROMPT: 'PRRSurveyPrompt',
  RAG_AGENT_RESPONSE: 'RAGAgentResponse',
  RAG_AGENT_CLARIFICATION: 'RAGAgentClarification',
  SIGN_IN_PROMPT: 'SignInPrompt',
};

// Every message produced by the RAG (LLM) agent carries the AI disclaimer:
// the answer, a clarifying question, and its no-answer apology. Curated,
// safety, and system messages are not in this set.
const AI_GENERATED_TRANSCRIPT_NAMES = new Set([
  TRANSCRIPT_NAMES.RAG_AGENT_RESPONSE,
  TRANSCRIPT_NAMES.RAG_AGENT_CLARIFICATION,
]);

const getFeedbackTargetMessageId = activity => {
  const feedbackTargetMessageId =
    activity?.channelData?.feedbackTargetMessageId;

  return typeof feedbackTargetMessageId === 'string' &&
    feedbackTargetMessageId.trim()
    ? feedbackTargetMessageId
    : null;
};

const getStableMessageId = activity => {
  const messageId = activity?.channelData?.messageId;

  if (typeof messageId === 'string' && messageId.trim()) {
    return messageId;
  }

  return activity?.id;
};

/**
 * Maps a Direct Line activity into the chatbot message shape.
 * We only map text-based `message` activities so transcript state remains
 * deterministic and free of transport/debug events (`trace`, `event`, etc.).
 * @param {DirectLineActivityLike} activity
 * @returns {ChatMessage | null}
 */
export const mapDirectLineActivityToChatMessage = activity => {
  if (activity?.type !== ACTIVITY_TYPES.MESSAGE) {
    return null;
  }

  const sender = activity?.from?.role;
  const text = typeof activity?.text === 'string' ? activity.text.trim() : '';

  if (!text || (sender !== SENDER_TYPES.BOT && sender !== SENDER_TYPES.USER)) {
    return null;
  }

  // Selecting an icon (a feedback thumb) posts its name to the bot, but the
  // live UI never shows that as something the user said. Replayed history must
  // not show it either.
  if (
    sender === SENDER_TYPES.USER &&
    (activity?.channelData?.isFeedback ||
      Object.hasOwn(ICON_OPTION_LABELS, text))
  ) {
    return null;
  }

  const mappedMessage = {
    id:
      sender === SENDER_TYPES.BOT ? getStableMessageId(activity) : activity.id,
    sender,
    text,
  };

  if (sender === SENDER_TYPES.BOT) {
    mappedMessage.options = getActivityOptions(activity);
    mappedMessage.optionType = getActivityOptionType(activity);

    const transcriptName = activity?.channelData?.transcript_data?.name;
    const feedbackTargetMessageId = getFeedbackTargetMessageId(activity);

    // RAG (LLM) generated messages carry the AI disclaimer; curated, safety,
    // and system messages do not. The backend tags each RAG message with a
    // `RAGAgent*` transcript name.
    if (AI_GENERATED_TRANSCRIPT_NAMES.has(transcriptName)) {
      mappedMessage.isAiGenerated = true;
    }

    if (transcriptName === TRANSCRIPT_NAMES.PRR_SURVEY_PROMPT) {
      mappedMessage.isFeedback = true;
    }

    // Sign-in prompt: renders Login or Yes/No buttons; the frontend opens the
    // sign-in modal when an affirmative option is selected.
    if (transcriptName === TRANSCRIPT_NAMES.SIGN_IN_PROMPT) {
      mappedMessage.isSignInPrompt = true;
    }

    if (mappedMessage.isFeedback && feedbackTargetMessageId) {
      mappedMessage.feedbackTargetMessageId = feedbackTargetMessageId;
    }

    if (
      transcriptName === TRANSCRIPT_NAMES.PRR_SURVEY_ACKNOWLEDGEMENT &&
      feedbackTargetMessageId
    ) {
      mappedMessage.feedbackAcknowledgementTargetMessageId =
        feedbackTargetMessageId;
    }
  }

  return mappedMessage;
};

export const isTypingActivity = activity => {
  // Only bot typing should drive UI typing state. User/device typing activities
  // do not represent backend response progress and would create noisy indicators.
  return (
    activity?.type === ACTIVITY_TYPES.TYPING &&
    activity?.from?.role === SENDER_TYPES.BOT
  );
};
